/**
 * Business Rules Filtering Utilities
 * 
 * Provides utilities for applying organization-level business rules
 * as hard filters on vendor discovery queries.
 */

// Verification level ordering for comparison
export const VERIFICATION_LEVELS = ['none', 'basic', 'enhanced', 'premium'] as const;
export type VerificationLevel = typeof VERIFICATION_LEVELS[number];

/**
 * Get all verification levels that meet or exceed the minimum requirement
 * @param minimum - The minimum required verification level
 * @returns Array of eligible verification levels
 * @example getEligibleVerificationLevels('basic') → ['basic', 'enhanced', 'premium']
 */
export function getEligibleVerificationLevels(minimum: VerificationLevel): VerificationLevel[] {
  const minIndex = VERIFICATION_LEVELS.indexOf(minimum);
  if (minIndex === -1) return [...VERIFICATION_LEVELS];
  return [...VERIFICATION_LEVELS.slice(minIndex)];
}

/**
 * Business rules filter interface for vendor filtering
 */
export interface BusinessRulesFilter {
  minimum_vendor_verification_level?: VerificationLevel;
  minimum_vendor_rating?: number;
  minimum_vendor_completed_jobs?: number;
  required_certification_ids?: string[];
  required_capability_ids?: string[];
}

/**
 * Check if any business rules filters are active
 */
export function hasActiveBusinessRules(rules?: BusinessRulesFilter | null): boolean {
  if (!rules) return false;
  
  return Boolean(
    (rules.minimum_vendor_verification_level && rules.minimum_vendor_verification_level !== 'none') ||
    (rules.minimum_vendor_rating && rules.minimum_vendor_rating > 0) ||
    (rules.minimum_vendor_completed_jobs && rules.minimum_vendor_completed_jobs > 0) ||
    (rules.required_certification_ids && rules.required_certification_ids.length > 0) ||
    (rules.required_capability_ids && rules.required_capability_ids.length > 0)
  );
}
